import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { boxBudgetCopy, type ProvisionAnswer } from '../lib/admin-provision';
import { absolute } from '../lib/time';
import { CopySnippet } from './CopySnippet';

// What a provision DID (plan 102 slice 05), each effect said on its own because
// "provisioned" is not one fact (`provisionResponse`, `apps/api/src/routes/admin.ts`):
//   - the invitation: whether its mail LEFT (`mailed`, never a delivery claim), the
//     one-time accept link (no listing ever carries it), its expiry;
//   - the approval: approved now, already approved, or not yet registered — in which
//     case there is NO grant yet: the capacity is booked on the invitation;
//   - the grants the person now holds, kind as the machine names it, and the box
//     budget they imply — the server's `boxes`, `null` read as unlimited.
// Presenter for `<AdminInviteByEmailDialog/>`.

export function AdminProvisionResult(props: { readonly result: ProvisionAnswer }): JSX.Element {
  const email = (): string => props.result.email ?? '';
  // Literal `t()` per arm, so `i18n.coverage.test.ts` sees every key.
  const approvalLine = (): string => {
    switch (props.result.approval) {
      case 'approved':
        return t('admin.provision_approved');
      case 'already_approved':
        return t('admin.provision_already_approved');
      case 'not_yet_registered':
        return t('admin.provision_booked');
    }
  };
  return (
    <div class="field-group">
      <Show when={props.result.invitation} keyed>
        {(invitation) => (
          <>
            <p role="status">
              {t(
                invitation.mailed ? 'admin.access_invited_mail' : 'admin.access_invited_not_mailed',
                { email: email() },
              )}
            </p>
            <Show when={invitation.reissued}>
              <p class="field__desc">{t('admin.access_invited_reissued')}</p>
            </Show>
            <CopySnippet label={t('admin.access_accept_link')} snippet={invitation.acceptUrl} />
            <p class="field__desc">
              {t('admin.access_invited_expires', { at: absolute(invitation.expiresAtIso) })}
            </p>
          </>
        )}
      </Show>
      <p class="field__desc">{approvalLine()}</p>
      <p class="field__desc" data-testid="provision-boxes">
        {boxBudgetCopy(props.result.boxes)}
      </p>
      <Show when={props.result.grants.length > 0}>
        <ul class="row-list" aria-label={t('admin.user_grants_heading')}>
          <For each={props.result.grants}>
            {(grant) => (
              <li class="row-list__row">
                <span class="admin-row__stack">
                  <code>{grant.kind}</code>
                  <Show when={grant.amount !== null}>
                    <span class="admin-row__meta">
                      {t('admin.grants_amount_value', { amount: String(grant.amount) })}
                    </span>
                  </Show>
                </span>
                <span class="admin-row__meta">
                  {grant.expires_at_iso === null
                    ? t('admin.provision_grant_ongoing')
                    : t('admin.provision_grant_until', { at: absolute(grant.expires_at_iso) })}
                </span>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </div>
  );
}
